import { AppFooter } from '@/components/layout/AppFooter';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';
import logoFull from '@/assets/logo-full.svg';
import { useState, useEffect } from 'react';

const TermsOfService = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('section1');

  const sections = [
    { id: 'section1', key: 'section1' },
    { id: 'section2', key: 'section2' },
    { id: 'section3', key: 'section3' },
    { id: 'section4', key: 'section4' },
    { id: 'section5', key: 'section5' },
    { id: 'section6', key: 'section6' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      const sectionElements = sections.map(s => ({
        id: s.id,
        element: document.getElementById(s.id),
      }));

      const currentSection = sectionElements.find(({ element }) => {
        if (!element) return false;
        const rect = element.getBoundingClientRect();
        return rect.top >= 0 && rect.top <= window.innerHeight / 2;
      });

      if (currentSection) {
        setActiveSection(currentSection.id);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-white sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              {t('common.back')}
            </Button>
            <img src={logoFull} alt="Velox" className="h-8" />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="lg:grid lg:grid-cols-4 lg:gap-8">
          {/* Table of Contents - Sticky Sidebar */}
          <aside className="hidden lg:block lg:col-span-1">
            <div className="sticky top-24">
              <Card className="rounded-xl shadow-md">
                <CardHeader className="pb-4">
                  <CardTitle className="font-heading text-lg text-secondary flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    {t('common.contents')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-1">
                  {sections.map((section) => (
                    <button
                      key={section.id}
                      onClick={() => scrollToSection(section.id)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                        activeSection === section.id
                          ? 'bg-accent text-accent-foreground font-medium'
                          : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground'
                      }`}
                    >
                      {t(`legal.termsOfService.${section.key}.title`)}
                    </button>
                  ))}
                </CardContent>
              </Card>
            </div>
          </aside>

          {/* Content */}
          <div className="lg:col-span-3">
            <Card className="rounded-2xl shadow-lg">
              <CardHeader className="text-center border-b pb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-accent/10 mb-4 mx-auto">
                  <FileText className="h-8 w-8 text-accent" />
                </div>
                <CardTitle className="font-heading text-3xl md:text-4xl text-secondary mb-3">
                  {t('legal.termsOfService.title')}
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  {t('legal.termsOfService.lastUpdate')}: 13/11/2025
                </p>
              </CardHeader>
              
              <CardContent className="p-6 md:p-8 space-y-8">
                {sections.map((section) => (
                  <section 
                    key={section.id}
                    id={section.id}
                    className="scroll-mt-24"
                  >
                    <h2 className="text-xl md:text-2xl font-semibold text-secondary mb-4 pb-2 border-b">
                      {t(`legal.termsOfService.${section.key}.title`)}
                    </h2>
                    <p className="text-muted-foreground leading-relaxed text-justify">
                      {t(`legal.termsOfService.${section.key}.content`)}
                    </p>
                  </section>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      <AppFooter variant="app" />
    </div>
  );
};

export default TermsOfService;
